import moment from 'moment'
import momentHijri from 'moment-hijri'

/**
 * Standard date formats supported by the application.
 */
const supportedFormats = [
  moment.ISO_8601,
  "YYYY-MM-DD",
  "DD-MM-YYYY",
  "DD/MM/YYYY",
  "MM-DD-YYYY",
  "MM-DD-YY",
  "DD-MM-YY",
  "YYYY/MM/DD",
  "MM/DD/YYYY",
  "YYYYMMDD"
];

/**
 * Converts Hijri strings (or i-prefixed Gregorian) to a normalized Gregorian date.
 * Handles the 'iYYYY-iMM-iDD' format and includes normalization for years < 1000.
 */
const convertHijriToGregorian = (dateString) => {
  if (!dateString) return null
  
  const dateStr = String(dateString).trim()
  
  // Match iYYYY-iMM-iDD format
  if (dateStr.match(/^i\d{4}-i\d{2}-i\d{2}$/i)) {
    const cleanDate = dateStr.replace(/i/gi, '')
    const [year, month, day] = cleanDate.split('-').map(Number)    
    
    // Years > 1900 are treated as Gregorian
    if (year >= 1500 && year <= 2100) {
      return cleanDate
    } else {
      // Normalize Hijri year if needed (e.g. 445 -> 1445)
      const hijriYear = year < 1000 ? year + 1000 : year
      const hijriMoment = momentHijri()
      hijriMoment.iYear(hijriYear)
      hijriMoment.iMonth(month - 1) 
      hijriMoment.iDate(day)
      
      if (hijriMoment.isValid()) {
        return hijriMoment.format('YYYY-MM-DD')
      }
    }
  }
  
  return dateString
}

/**
 * Returns a formatted age string. 
 * Re-integrated spaces (e.g. " Y", " M") to ensure no regressions in displays or reports.
 */
export const CalculateAge = (birthDate, asOfDate) => {
  if (!birthDate) return "0 D"

  const gregorianDate = convertHijriToGregorian(birthDate)
  
  // Try parsing with the normalized string
  let dob = moment(gregorianDate, supportedFormats, false)
  
  // If invalid, try the original cleanup behavior (removing letters)
  if (!dob.isValid()) {
    const cleanInput = String(birthDate).replace(/[a-zA-Z]/g, "")
    dob = moment(cleanInput, supportedFormats, false)
  }

  // If still invalid, return original string or fallback
  if (!dob.isValid()) return birthDate || "0 D"

  const reference = asOfDate
    ? moment.parseZone(asOfDate, [moment.ISO_8601, 'DD MMM YYYY hh:mm A'], true)
    : moment()
  if (!reference.isValid()) return undefined
  const today = asOfDate
    ? moment(reference.format('YYYY-MM-DD'), 'YYYY-MM-DD')
    : reference
  const years = today.diff(dob, 'years')
  const afterYears = dob.clone().add(years, "years")
  const months = today.diff(afterYears, 'months')
  const afterMonths = afterYears.clone().add(months, "months")
  const days = today.diff(afterMonths, 'days')

  if (years > 100) return "100 Y"
  if (years > 0) return `${years} Y`
  if (months > 0) return `${months} M`
  if (days > 0) return `${days} D`

  return "1 D"
}

/**
 * Returns age in years as a number.
 */
export const CalculateAgeInYears = (birthDate) => {
  if (!birthDate) return 0

  const gregorianDate = convertHijriToGregorian(birthDate)
  const dob = moment(gregorianDate, supportedFormats, false)

  if (!dob.isValid()) return 0

  const today = moment()
  const years = today.diff(dob, 'years')
  if (years > 100) return 100
  return years < 0 ? 0 : years
}

/**
 * Safely parses input into a native Date object.
 */
export const parseDateSafe = (input) => {
  if (!input) return null;
  if (input instanceof Date) return input;

  if (!isNaN(input) && typeof input !== 'boolean' && String(input).trim() !== "") {
    const d = new Date(Number(input));
    if (!isNaN(d.getTime())) return d;
  }

  let dateStr = String(input).trim();
  
  // Handle Hijri/prefixed format
  if (dateStr.match(/^i\d{4}-i\d{2}-i\d{2}$/i)) {
    const cleanDate = dateStr.replace(/i/gi, '')
    const [year, month, day] = cleanDate.split('-').map(Number)
    if (year >= 1500 && year <= 2100) {
      return new Date(year, month - 1, day)
    } else {
      const hijriYear = year < 1000 ? year + 1000 : year
      const hijriMoment = momentHijri()
      hijriMoment.iYear(hijriYear)
      hijriMoment.iMonth(month - 1)
      hijriMoment.iDate(day)
      if (hijriMoment.isValid()) return hijriMoment.toDate()
    }
  }

  // Legacy character-stripping fallback
  const cleaned = dateStr.replace(/[a-zA-Z]/g, "")
  const native = new Date(cleaned)
  if (!isNaN(native.getTime())) return native

  // Native parse fallback for the original string
  const nativeOrig = new Date(dateStr)
  if (!isNaN(nativeOrig.getTime())) return nativeOrig

  // Fallback for parts
  const parts = cleaned.split(/[/-]/)
  if (parts.length === 3) {
    let [a, b, c] = parts.map(Number)
    if (a > 31) return new Date(a, b - 1, c)
    else if (c > 31) return new Date(c, b - 1, a)
  }

  return null;
};

/**
 * Detailed age object calculation.
 */
export const calculateDetailedAge = (birthDate) => {
  const birth = parseDateSafe(birthDate);
  if (!birth) return { years: 0, months: 0, days: 0 };

  const today = new Date();

  let years = today.getFullYear() - birth.getFullYear();
  let months = today.getMonth() - birth.getMonth();
  let days = today.getDate() - birth.getDate();

  if (days < 0) {
    months--;
    const prevMonth = new Date(today.getFullYear(), today.getMonth(), 0);
    days += prevMonth.getDate();
  }

  if (months < 0) {
    years--;
    months += 12;
  }

  if (years > 100) return { years: 100, months: 0, days: 0 };

  return { years: years < 0 ? 0 : years, months: months < 0 ? 0 : months, days: days < 0 ? 0 : days };
};
